import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  useRouterState,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";
import { captureReferralCode, clearReferralCode } from "./lib/referral";
import { captureSubscribePromoCode, clearSubscribePromoCode } from "./lib/subscribe-promo";
import { BottomNav } from "./components/bottom-nav";
import { VenueHeader } from "./components/venue-header";
import { VenueAppShell } from "./components/venue-app-shell";
import { VenueWebLayout } from "./components/venue-web-layout";
import { ArtistSite, ArtistGalleryPage, ArtistNewsPage } from "./components/artist-site";
import { PromoterSite } from "./components/promoter-site";
import { SitePagesSite } from "./components/site-pages";
import { shouldRenderSitePages, shouldRenderAdminSessions, shouldRenderComingSoon } from "./lib/router-decisions";
import { usePreviewOverlay } from "./lib/preview-overlay";
import { PullToRefresh } from "./components/pull-to-refresh";
import { OfflineBanner } from "./components/offline-banner";
import { VenueShell } from "./components/venue-shell";
import { DraftGate, DraftBanner } from "./components/draft-gate";
import { RouteErrorFallback } from "./components/route-error-fallback";
import { RecordWebLayout } from "./components/record-web-layout";
import { FestivalWebLayout } from "./components/festival-web-layout";
import { useVenue } from "./lib/venue";
import { isNativeApp } from "./lib/platform";
import { useAuth } from "./lib/auth";
import { PushPromptModal } from "./components/push-prompt-modal";
import { WelcomePushPrompt } from "./components/welcome-push-prompt";
import { PostAuthPushRegister } from "./components/post-auth-push-register";
import { OsPermissionSyncHook } from "./components/os-permission-sync-hook";
import { BadgeClearHook } from "./components/badge-clear-hook";
import { PushOpenQueueFlusher } from "./components/push-open-queue-flusher";
// Eager (in the entry chunk): the core landing path. These render as JSX inside
// the venue-home / festival / tickets orchestrators below, so they can't suspend
// mid-render, and they're the pages a first visitor lands on, keep them fast.
import { VenuePage } from "./pages/venue";
import { TicketsPage } from "./pages/tickets";
import { FestivalPage, type FestivalVariant } from "./pages/festival";
import { Navigate } from "@tanstack/react-router";
import { RadioShell } from "./components/radio-shell";
import {
  RadioHomePage,
  RadioSchedulePage,
  RadioShowsPage,
  RadioShopPage,
  RadioSupportPage,
} from "./pages/radio-home";
import { ComingSoonSplash } from "./pages/coming-soon-splash";

// Lazy routes: each page is its own chunk, fetched on navigation and prefetched
// on hover/touch (defaultPreload:"intent" below). This keeps the initial load,
// especially the public marketing/venue/festival landing, from parsing the
// whole app (the ~20-page admin cluster, commerce, wallet, QR scanning) up front.
const AllTiersPage = lazyRouteComponent(() => import("./pages/tiers"), "AllTiersPage");
const BenefitsPage = lazyRouteComponent(() => import("./pages/benefits"), "BenefitsPage");
const DownloadPage = lazyRouteComponent(() => import("./pages/download"), "DownloadPage");
const NoticeboardPage = lazyRouteComponent(() => import("./pages/noticeboard"), "NoticeboardPage");
const SignInPage = lazyRouteComponent(() => import("./pages/sign-in"), "SignInPage");
const SignUpPage = lazyRouteComponent(() => import("./pages/sign-up"), "SignUpPage");
const AccountPage = lazyRouteComponent(() => import("./pages/account"), "AccountPage");
const BookingsPage = lazyRouteComponent(() => import("./pages/bookings"), "BookingsPage");
const TrialPage = lazyRouteComponent(() => import("./pages/trial"), "TrialPage");
const ProfilePage = lazyRouteComponent(() => import("./pages/profile"), "ProfilePage");
const AdminPage = lazyRouteComponent(() => import("./pages/admin"), "AdminPage");
const AdminBrandingPage = lazyRouteComponent(() => import("./pages/admin-branding"), "AdminBrandingPage");
const AdminMediaPage = lazyRouteComponent(() => import("./pages/admin-media"), "AdminMediaPage");
const AdminSmsPage = lazyRouteComponent(() => import("./pages/admin-sms"), "AdminSmsPage");
const AdminNoticeboardPage = lazyRouteComponent(() => import("./pages/admin-noticeboard"), "AdminNoticeboardPage");
const AdminDiscountCodesPage = lazyRouteComponent(() => import("./pages/admin-discount-codes"), "AdminDiscountCodesPage");
const TicketDetailPage = lazyRouteComponent(() => import("./pages/ticket-detail"), "TicketDetailPage");
const BookEntry = lazyRouteComponent(() => import("./pages/book-entry"), "BookEntry");
const EventsPage = lazyRouteComponent(() => import("./pages/events"), "EventsPage");
const EventDetailPage = lazyRouteComponent(() => import("./pages/event-detail"), "EventDetailPage");
const MenuPage = lazyRouteComponent(() => import("./pages/menu"), "MenuPage");
const RecordDetailPage = lazyRouteComponent(() => import("./pages/record-detail"), "RecordDetailPage");
const CartPage = lazyRouteComponent(() => import("./pages/cart"), "CartPage");
const WishlistPage = lazyRouteComponent(() => import("./pages/wishlist"), "WishlistPage");
const AdminInventoryPage = lazyRouteComponent(() => import("./pages/admin-inventory"), "AdminInventoryPage");
const AdminRadioPage = lazyRouteComponent(() => import("./pages/admin-radio"), "AdminRadioPage");
const AdminStorefrontPage = lazyRouteComponent(() => import("./pages/admin-storefront"), "AdminStorefrontPage");
const AdminOffersPage = lazyRouteComponent(() => import("./pages/admin-offers"), "AdminOffersPage");
const SellPage = lazyRouteComponent(() => import("./pages/sell"), "SellPage");
const AdminBenefitsPage = lazyRouteComponent(() => import("./pages/admin-benefits"), "AdminBenefitsPage");
const AdminSubscribersPage = lazyRouteComponent(() => import("./pages/admin-subscribers"), "AdminSubscribersPage");
const AdminTagsPage = lazyRouteComponent(() => import("./pages/admin-tags"), "AdminTagsPage");
const AdminEventsPage = lazyRouteComponent(() => import("./pages/admin-events"), "AdminEventsPage");
const AdminEventAttendeesPage = lazyRouteComponent(() => import("./pages/admin-event-attendees"), "AdminEventAttendeesPage");
const AdminScanPage = lazyRouteComponent(() => import("./pages/admin-scan"), "AdminScanPage");
const TicketPublicPage = lazyRouteComponent(() => import("./pages/ticket-public"), "TicketPublicPage");
const AdminTeamPage = lazyRouteComponent(() => import("./pages/admin-team"), "AdminTeamPage");
const AdminServicesPage = lazyRouteComponent(() => import("./pages/admin-services"), "AdminServicesPage");
const AdminSessionsPage = lazyRouteComponent(() => import("./pages/admin-sessions"), "AdminSessionsPage");
const AdminTrafficPage = lazyRouteComponent(() => import("./pages/admin-traffic"), "AdminTrafficPage");
const AdminFansPage = lazyRouteComponent(() => import("./pages/admin-fans"), "AdminFansPage");
const AdminFanPage = lazyRouteComponent(() => import("./pages/admin-fan"), "AdminFanPage");
const AdminSegmentsPage = lazyRouteComponent(() => import("./pages/admin-segments"), "AdminSegmentsPage");
const AdminTrackedLinksPage = lazyRouteComponent(() => import("./pages/admin-tracked-links"), "AdminTrackedLinksPage");
const AdminSettingsPage = lazyRouteComponent(() => import("./pages/admin-settings"), "AdminSettingsPage");
const AdminCampaignsPage = lazyRouteComponent(() => import("./pages/admin-campaigns"), "AdminCampaignsPage");
const AdminWelcomeEmailPage = lazyRouteComponent(() => import("./pages/admin-welcome-email"), "AdminWelcomeEmailPage");
const AdminLoyaltyPage = lazyRouteComponent(() => import("./pages/admin-loyalty"), "AdminLoyaltyPage");
const AdminMenuPage = lazyRouteComponent(() => import("./pages/admin-menu"), "AdminMenuPage");
const AdminPosPage = lazyRouteComponent(() => import("./pages/admin-pos"), "AdminPosPage");
const AdminPosKdsPage = lazyRouteComponent(() => import("./pages/admin-pos-kds"), "AdminPosKdsPage");
const AdminPosOrdersPage = lazyRouteComponent(() => import("./pages/admin-pos-orders"), "AdminPosOrdersPage");
const AdminFloorPage = lazyRouteComponent(() => import("./pages/admin-floor"), "AdminFloorPage");
const AdminRevenuePage = lazyRouteComponent(() => import("./pages/admin-revenue"), "AdminRevenuePage");
const AdminAnalyticsPage = lazyRouteComponent(() => import("./pages/admin-analytics"), "AdminAnalyticsPage");
const AdminCalendarPage = lazyRouteComponent(() => import("./pages/admin-calendar"), "AdminCalendarPage");
const ForgotPasswordPage = lazyRouteComponent(() => import("./pages/forgot-password"), "ForgotPasswordPage");
const ResetPasswordPage = lazyRouteComponent(() => import("./pages/reset-password"), "ResetPasswordPage");
const BookServicePage = lazyRouteComponent(() => import("./pages/book-service"), "BookServicePage");
const NfcTapPage = lazyRouteComponent(() => import("./pages/nfc-tap"), "NfcTapPage");
const PreferencesPage = lazyRouteComponent(() => import("./pages/preferences"), "PreferencesPage");
const AdminNotificationsPage = lazyRouteComponent(() => import("./pages/admin-notifications"), "AdminNotificationsPage");
const NotificationsPage = lazyRouteComponent(() => import("./pages/notifications"), "NotificationsPage");
const AdminFestivalPage = lazyRouteComponent(() => import("./pages/admin-festival"), "AdminFestivalPage");
const AdminWebsitePage = lazyRouteComponent(() => import("./pages/admin-website"), "AdminWebsitePage");
const AdminMailingListsPage = lazyRouteComponent(() => import("./pages/admin-mailing-lists"), "AdminMailingListsPage");
const AdminOrdersPage = lazyRouteComponent(() => import("./pages/admin-orders"), "AdminOrdersPage");

function RootComponent() {
  useEffect(() => {
    captureReferralCode();
    captureSubscribePromoCode();
    if (new URLSearchParams(window.location.search).get("subscribed") === "true") {
      clearReferralCode();
      clearSubscribePromoCode();
    }
  }, []);

  // Record shops render as a WEBSITE in a browser (top nav + hero + footer,
  // document scroll) and as the APP inside the native shell. Everything else
  // uses the app shell as before.
  const { data } = useVenue();
  // Push-prompt modal: fires once for signed-in users who haven't yet
  // decided about notifications. The modal fetches its own decision
  // from the server + renders nothing when show=false, so it's cheap
  // to mount unconditionally alongside every layout branch below.
  // Signed-out visitors: fetch returns 401, decision is null,
  // component renders nothing.
  const { user: authUser } = useAuth();
  const venueIdForPrompt = data?.venue.id ?? "";
  const venueNameForPrompt = data?.venue.appName ?? data?.venue.name ?? "";
  const signedIn = !!authUser;
  const pushPrompt =
    signedIn && venueIdForPrompt ? (
      <>
        <PushPromptModal
          venueId={venueIdForPrompt}
          venueName={venueNameForPrompt}
          signedIn={true}
        />
        {/* Runs the post-signup register flow: reads the pre-auth
            localStorage choice + current OS permission, silently
            registers the token if granted, records the denial if
            denied, else defers to the signed-in modal above. */}
        <PostAuthPushRegister
          userId={authUser?.id ?? null}
          venueId={venueIdForPrompt}
        />
        {/* Foreground OS-permission sync: detects when the user
            toggles notifications in iOS/Android Settings and
            reconciles server state (registers a fresh token on
            re-enable, revokes on disable). No-op in browsers +
            when signed out. */}
        <OsPermissionSyncHook
          userId={authUser?.id ?? null}
          venueId={venueIdForPrompt}
        />
        {/* Clear the iOS app-icon badge (and Notification Center)
            when the user opens or foregrounds the app. Fenton
            2026-09-30: Love Inn home-screen icon kept a "2" badge
            after the user viewed pushes. */}
        <BadgeClearHook />
        {/* Flush push-open beacons that got queued at cold-launch
            when the session cookie wasn't yet valid. Peer-review
            2026-09-29 HIGH: without this, most tonight's Silva
            Bumpa lock-screen taps would drop silently and the
            admin dashboard would read 0% open rate even when
            everyone tapped. */}
        <PushOpenQueueFlusher userId={authUser?.id ?? null} />
      </>
    ) : venueIdForPrompt ? (
      /* Signed-out native visitor: welcome prompt captures the OS
         permission grant NOW so post-signup we can register the
         token silently. Browser signed-out visitors see nothing
         (WelcomePushPrompt gates on isNativeApp internally). */
      <WelcomePushPrompt
        venueId={venueIdForPrompt}
        venueName={venueNameForPrompt}
        signedOut={true}
      />
    ) : null;
  const website = data?.venue.archetype === "RECORD_SHOP" && !isNativeApp();
  // Festival venues render as a marketing WEBSITE in the browser (top nav +
  // hero + footer), and as the app inside the native shell, mirrors record
  // shops. Which event shows at the root comes from appConfig.landingFestivalEventId.
  const festivalSite = data?.venue.archetype === "FESTIVAL" && !isNativeApp();
  // Radio stations render as a full-bleed marketing WEBSITE (the RadioHome page
  // carries its own header/footer), like festivals + record shops.
  const radioSite = data?.venue.archetype === "RADIO" && !isNativeApp();
  // OPT-IN Assembly-managed website for any OTHER archetype (hospitality,
  // wellness, studio, artist, promoter). Only venues that have explicitly turned
  // this on (appConfig.websiteMode) get the generic website in a browser,
  // everyone else keeps their current app-style home untouched. Never affects an
  // existing live venue unless it opts in. A coming-soon landing page takes
  // precedence, it IS that venue's website (full-page design, e.g. Toka), so
  // don't wrap it in the web chrome/hero.
  const pathname = useRouterState({ select: (st) => st.location.pathname });
  const websiteModeValue = data?.venue.appConfig?.websiteMode;
  const websiteMode =
    (websiteModeValue === true || websiteModeValue === "promo") &&
    data?.venue.appConfig?.comingSoon?.enabled !== true &&
    !isNativeApp();

  // Coming-soon landing IS the venue's public site (full-page design, e.g.
  // Toka). Every non-admin, non-auth browser path renders the splash: a
  // deep-linked visitor to /noticeboard or /book must NOT see the app-shell
  // (fixed-viewport native layout, breaks Safari scroll semantics, Fenton
  // flagged 2026-09-30 on Toka mobile). Native app keeps the app-shell so
  // a launched-app venue with browser-side coming-soon still works.
  // Decision extracted to shouldRenderComingSoon (see router-decisions +
  // tests/unit/router-decisions.test.ts).
  const location = useRouterState({ select: (st) => st.location });
  if (shouldRenderComingSoon({
    comingSoonEnabled: data?.venue.appConfig?.comingSoon?.enabled === true,
    pathname,
    isNativeApp: isNativeApp(),
    // Preserve Stripe checkout return URLs (?purchased=, ?booked=,
    // ?checkout=) so a completed transaction shows its receipt even
    // when the venue is otherwise in coming-soon mode.
    search: location.searchStr,
  })) {
    return (
      <DraftGate>
        <VenueAppShell variant="web">
          <DraftBanner />
          <OfflineBanner />
          <ComingSoonSplash />
        </VenueAppShell>
      </DraftGate>
    );
  }

  // MULTI-PAGE white-label site: a venue with configured pages
  // (appConfig.sitePages) serves its whole page set (home + /whats-on +
  // /venue-hire + …) from the block renderer, the builder-designed website
  // (Love Inn first). Any configured slug is intercepted here BEFORE the
  // archetype-specific layouts below so FESTIVAL + RADIO + record-shop
  // venues can host sitePages too , previously the archetype layouts
  // returned first and the splat route at `path: "$"` redirected unknown
  // slugs home. Fenton @ Madruga 2026-10-09: /accommodation "flashed and
  // went back" because the festival template won the intercept race +
  // nothing matched inside its Outlet.
  //
  // The rules (encoded in shouldRenderSitePages, unit-tested in
  // tests/unit/router-decisions.test.ts):
  //   - Native app NEVER intercepts. Load-bearing (Love Inn iOS
  //     regression 2026-09-06 — PR #184).
  //   - Browser HOME ("/") intercepts only when websiteMode is on. For
  //     Madruga + any FESTIVAL venue without websiteMode set, "/" falls
  //     through to the FestivalWebLayout below and renders the bespoke
  //     festival landing.
  //   - Browser non-home intercepts whenever the sitePages config has a
  //     matching slug (Madruga's /accommodation; Prospect's
  //     /account-deletion, /privacy, /terms…).
  const sitePagesCfg = data?.venue.appConfig?.sitePages;
  const inPreview = usePreviewOverlay() !== null;
  if (shouldRenderSitePages({ sitePages: sitePagesCfg, pathname, isNativeApp: isNativeApp(), websiteMode, includeDrafts: inPreview })) {
    // FESTIVAL venues: wrap the sitePages render in FestivalWebLayout so the
    // top nav (logo + sitePages links + Buy tickets CTA) matches the festival
    // home exactly, one unified chrome across every route. SitePagesSite
    // renders its blocks only (chromeOff=true), FestivalWebLayout owns the
    // header + footer. Fenton @ Madruga 2026-10-10: /accommodation was
    // showing sitePages' own simpler nav; he wanted parity with the festival
    // ticket page.
    if (festivalSite) {
      return (
        <DraftGate>
          <VenueAppShell variant="web">
            <DraftBanner />
            <OfflineBanner />
            <FestivalWebLayout>
              <SitePagesSite pathname={pathname} chromeOff />
            </FestivalWebLayout>
            {pushPrompt}
          </VenueAppShell>
        </DraftGate>
      );
    }
    return (
      <DraftGate>
        <VenueAppShell variant="web">
          <DraftBanner />
          <OfflineBanner />
          <SitePagesSite pathname={pathname} />
          {pushPrompt}
        </VenueAppShell>
      </DraftGate>
    );
  }

  if (website) {
    return (
      <DraftGate>
        <VenueAppShell variant="web">
          <DraftBanner />
          <OfflineBanner />
          <RecordWebLayout>
            <Outlet />
          </RecordWebLayout>
          {pushPrompt}
        </VenueAppShell>
      </DraftGate>
    );
  }

  if (festivalSite) {
    return (
      <DraftGate>
        <VenueAppShell variant="web">
          <DraftBanner />
          <OfflineBanner />
          <FestivalWebLayout>
            <Outlet />
          </FestivalWebLayout>
          {pushPrompt}
        </VenueAppShell>
      </DraftGate>
    );
  }

  if (radioSite) {
    return (
      <DraftGate>
        <VenueAppShell variant="web">
          <DraftBanner />
          <OfflineBanner />
          <RadioShell>
            <Outlet />
          </RadioShell>
          {pushPrompt}
        </VenueAppShell>
      </DraftGate>
    );
  }

  // PROMOTER venues that opt in get the bespoke promoter site (MAS port:
  // splash video, crossfade hero, merch, past events, capture) at "/" only.
  if (websiteMode && data?.venue.archetype === "PROMOTER" && pathname === "/") {
    return (
      <DraftGate>
        <VenueAppShell variant="web">
          <DraftBanner />
          <OfflineBanner />
          <PromoterSite />
          {pushPrompt}
        </VenueAppShell>
      </DraftGate>
    );
  }

  // The PROMO SITE (full-bleed hero, release/announcement centrepiece, music,
  // gigs, gallery, capture), designed, not chrome around app cards. ARTIST
  // venues get it as their opted-in website; every other archetype can pick it
  // as a mode (websiteMode: "promo"). The promo page IS the home; every other
  // route (event pages, sign-in…) renders in the generic web layout below.
  if (
    websiteMode &&
    (websiteModeValue === "promo" || data?.venue.archetype === "ARTIST") &&
    (pathname === "/" || pathname === "/gallery" || pathname === "/news")
  ) {
    return (
      <DraftGate>
        <VenueAppShell variant="web">
          <DraftBanner />
          <OfflineBanner />
          {pathname === "/gallery" ? <ArtistGalleryPage /> : pathname === "/news" ? <ArtistNewsPage /> : <ArtistSite />}
          {pushPrompt}
        </VenueAppShell>
      </DraftGate>
    );
  }

  // A venue that has OPTED IN to an Assembly-managed website renders as a generic
  // white-label WEBSITE in a browser (web chrome + document scroll + footer), and
  // as the app inside the native shell. Venues that haven't opted in fall through
  // to their existing app-style home, unchanged.
  if (websiteMode) {
    return (
      <DraftGate>
        <VenueAppShell variant="web">
          <DraftBanner />
          <OfflineBanner />
          <VenueWebLayout>
            <Outlet />
          </VenueWebLayout>
          {pushPrompt}
        </VenueAppShell>
      </DraftGate>
    );
  }

  // Native App Store app, the app-shell experience (header + bottom nav).
  return (
    <DraftGate>
      <VenueAppShell>
        <VenueHeader />
        <DraftBanner />
        <OfflineBanner />
        <PullToRefresh>
          <VenueShell>
            <Outlet />
          </VenueShell>
        </PullToRefresh>
        <BottomNav />
        {pushPrompt}
      </VenueAppShell>
    </DraftGate>
  );
}

const rootRoute = createRootRoute({
  component: RootComponent,
  // Any render throw inside a route (Love Inn /events crash 2026-09-07)
  // is caught here, reported to /api/client-error so it shows up in the
  // Observability tab, and rendered as a graceful retry UI. Without this
  // TanStack's built-in CatchBoundary shows a bare "Something went
  // wrong!" and the error is swallowed before window.onerror can see it.
  errorComponent: RouteErrorFallback,
});

// Venue root: a festival splash (logo + sign-up) when the venue is a festival
// with a landing event; otherwise the default venue home.
function VenueHome() {
  const { data, isLoading } = useVenue();
  const landingEventId = data?.venue?.appConfig?.landingFestivalEventId;
  // Don't flash the default home before the venue config resolves (usually
  // instant, useVenue seeds from cache).
  if (!data && isLoading) return null;
  // Pre-launch "coming soon" splash (mailing-list capture) takes precedence,
  // a venue that isn't taking bookings/sales yet shows this instead of the home.
  if (data?.venue?.appConfig?.comingSoon?.enabled) return <ComingSoonSplash />;
  if (data?.venue?.archetype === "RADIO") return <RadioHomePage />;
  if (landingEventId) return <FestivalPage eventId={landingEventId} variant="splash" />;
  return <VenuePage />;
}

// A standalone festival page (Lineup / What's on / Info / Tickets), resolves
// the venue's landing event and renders that slice. Falls back to the venue
// home for non-festival venues (these routes are only linked from the festival
// site nav).
function FestivalRoute({ variant }: { variant: FestivalVariant }) {
  const { data } = useVenue();
  const eventId = data?.venue?.appConfig?.landingFestivalEventId;
  if (!eventId) return <VenuePage />;
  return <FestivalPage eventId={eventId} variant={variant} />;
}

// /tickets is the festival buy page for festival venues, and the ticket wallet
// for everyone else.
function TicketsRoute() {
  const { data } = useVenue();
  const eventId = data?.venue?.appConfig?.landingFestivalEventId;
  if (data?.venue?.archetype === "FESTIVAL" && eventId) {
    return <FestivalPage eventId={eventId} variant="tickets" />;
  }
  return <TicketsPage />;
}

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: VenueHome,
});

const tiersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tiers",
  component: AllTiersPage,
});

// Radio sub-pages (RADIO venues only). RadioShell (mounted by RootComponent for
// radio venues) provides the player context these consume; guard so a non-radio
// venue hitting these paths just bounces home instead of throwing.
function RadioGuard({ children }: { children: ReactNode }) {
  const { data, isLoading } = useVenue();
  if (!data && isLoading) return null;
  if (data?.venue?.archetype !== "RADIO") return <Navigate to="/" />;
  return <>{children}</>;
}
const radioScheduleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/schedule",
  component: () => (
    <RadioGuard>
      <RadioSchedulePage />
    </RadioGuard>
  ),
});
const radioShowsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/shows",
  component: () => (
    <RadioGuard>
      <RadioShowsPage />
    </RadioGuard>
  ),
});
const radioShopRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/shop",
  component: () => (
    <RadioGuard>
      <RadioShopPage />
    </RadioGuard>
  ),
});
const radioSupportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/support",
  component: () => (
    <RadioGuard>
      <RadioSupportPage />
    </RadioGuard>
  ),
});

const benefitsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/benefits",
  component: BenefitsPage,
});

const noticeboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/noticeboard",
  component: NoticeboardPage,
});

// /download: platform-aware app-store redirect. iOS -> App Store, Android
// -> Play, desktop -> QR + install buttons. Reads venue.appConfig.appStoreId
// / .playStoreId; falls back to a "coming soon" page if the relevant id
// isn't set. Used as the target of "Download the app" NFC tags (Issy at
// Love Inn, 2026-09-18) and works as a QR-code URL for posters + emails too.
const downloadRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/download",
  component: DownloadPage,
});

const trialRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/trial",
  component: TrialPage,
});

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sign-in",
  component: SignInPage,
});

const signUpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sign-up",
  component: SignUpPage,
});

const accountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/account",
  component: AccountPage,
});

const ticketsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tickets",
  component: TicketsRoute,
});

// Ticket wallet. On non-festival venues /tickets already IS the wallet, but on
// festival venues /tickets is the buy page, so buyers reach their purchased
// tickets here instead (linked from the festival site nav when signed in).
const myTicketsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/my-tickets",
  component: TicketsPage,
});

// Festival website pages (rendered inside FestivalWebLayout for festival venues).
const festivalLineupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/lineup",
  component: () => <FestivalRoute variant="lineup" />,
});
const festivalWhatsOnRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/whats-on",
  component: () => <FestivalRoute variant="whatsOn" />,
});
const festivalInfoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/info",
  component: () => <FestivalRoute variant="info" />,
});

const ticketDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tickets/$id",
  component: TicketDetailPage,
});

const bookingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/bookings",
  component: BookingsPage,
});

const bookRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/book",
  component: BookEntry,
});

const eventsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/events",
  component: EventsPage,
});

// Artist-site gallery (mosaic) + news (blog). The artist RootComponent branch
// renders them full-bleed; for any non-artist venue these routes bounce home.
const galleryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/gallery",
  component: () => <Navigate to="/" />,
});

const newsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/news",
  component: () => <Navigate to="/" />,
});

// Catch-all for builder-defined page slugs (/whats-on, /venue-hire, …): the
// RootComponent's sitePages branch intercepts configured slugs before this
// renders; anything unconfigured bounces home instead of a dead 404.
//
// Loading gate (Fenton @ Toka 2026-10-11): the splat used to render
// `<Navigate to="/">` unconditionally, which fired on the first paint
// BEFORE useVenue finished fetching the fresh appConfig. For a visitor
// landing directly on a newly-added sitePage (e.g. /safety-waiver), the
// seeded-from-localStorage venue data wouldn't contain the new slug yet,
// so RootComponent's shouldRenderSitePages returned false, the splat
// fired, and the URL bounced to / before the real sitePages data arrived.
// The user never saw the new page. Now the splat waits for useVenue to
// settle, and re-checks whether the fresh config contains the slug. If
// it does, RootComponent will have already short-circuited to render
// SitePagesSite and this splat never mounts; if it doesn't, Navigate home.
function SitePageSplatComponent() {
  const { data, isLoading, isFetching } = useVenue();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // Wait for the first real settle. During the stale-data window we'd
  // make a wrong Navigate decision; a null render is better than a
  // mis-redirect that erases the deep link.
  if (isLoading || isFetching) return null;
  const sitePages = data?.venue?.appConfig?.sitePages;
  const slug = pathname.replace(/^\/+/, "").replace(/\/+$/, "");
  const match = (sitePages?.pages ?? []).some((p) => (p?.slug ?? "") === slug);
  // If a sitePage matches, RootComponent's shouldRenderSitePages has
  // already short-circuited the whole tree, this component is only in
  // the Outlet when sitePages did NOT match. Still, guard explicitly:
  // nothing bad happens from rendering null in the brief overlap window.
  if (match) return null;
  return <Navigate to="/" />;
}
const sitePageSplatRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "$",
  component: SitePageSplatComponent,
});

const eventDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/events/$id",
  component: EventDetailPage,
});

const menuRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/menu",
  component: MenuPage,
});

const recordDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/records/$stockId",
  component: RecordDetailPage,
});

const cartRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/cart",
  component: CartPage,
});

const wishlistRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/wishlist",
  component: WishlistPage,
});

const adminInventoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/inventory",
  component: AdminInventoryPage,
});

const adminStorefrontRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/storefront",
  component: AdminStorefrontPage,
});

const adminRadioRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/radio",
  component: AdminRadioPage,
});

const adminOffersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/offers",
  component: AdminOffersPage,
});

const sellRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sell",
  component: SellPage,
});

// Both /benefits and /rewards render the SAME page (Issy 2026-09-17: the
// bottom-nav "Benefits" tab, NFC-tap deep links, and venue.tsx home tiles
// all still point at /rewards; the previous RewardsPage hard-coded generic
// "Free beer / Access to noticeboard" copy that didn't match the venue's
// actual configured benefits). Alias here rather than mass-repoint every
// link so legacy bookmarks + NFC tags keep working.
const rewardsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/rewards",
  component: BenefitsPage,
});

const profileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/profile",
  component: ProfilePage,
});

const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin",
  component: AdminPage,
});

const adminBrandingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/branding",
  component: AdminBrandingPage,
});

const adminMediaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/media",
  component: AdminMediaPage,
});

const adminSmsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/sms",
  component: AdminSmsPage,
});

const adminNoticeboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/noticeboard",
  component: AdminNoticeboardPage,
});

const adminDiscountCodesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/discount-codes",
  component: AdminDiscountCodesPage,
});

const adminBenefitsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/benefits",
  component: AdminBenefitsPage,
});

const adminSubscribersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/subscribers",
  component: AdminSubscribersPage,
});

const adminTagsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/tags",
  component: AdminTagsPage,
});

const adminEventsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/events",
  component: AdminEventsPage,
});

const adminEventAttendeesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/events/$id/attendees",
  component: AdminEventAttendeesPage,
});

const adminScanRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/scan",
  component: AdminScanPage,
});

// Public ticket view (no auth), for guests who bought without an account.
const ticketPublicRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ticket/$id",
  component: TicketPublicPage,
});

const adminTeamRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/team",
  component: AdminTeamPage,
});

const adminServicesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/services",
  component: AdminServicesPage,
});

// /admin/sessions is Toka's session-slot grid — WELLNESS-only. A non-WELLNESS
// admin would see an empty page (handlers are venue-scoped so they'd just
// return zero slots), which looked like a bug. Guard mirrors RadioGuard.
function SessionsGuard({ children }: { children: ReactNode }) {
  const { data, isLoading } = useVenue();
  if (!data && isLoading) return null;
  if (!shouldRenderAdminSessions(data?.venue?.archetype)) return <Navigate to="/admin" />;
  return <>{children}</>;
}
const adminSessionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/sessions",
  component: () => (
    <SessionsGuard>
      <AdminSessionsPage />
    </SessionsGuard>
  ),
});

const adminFansRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/fans",
  component: AdminFansPage,
});

const adminFanRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/fans/$email",
  component: AdminFanPage,
});

const adminSegmentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/segments",
  component: AdminSegmentsPage,
});

const adminCampaignsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/campaigns",
  component: AdminCampaignsPage,
});

const adminTrackedLinksRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/links",
  component: AdminTrackedLinksPage,
});

const adminWelcomeEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/welcome-email",
  component: AdminWelcomeEmailPage,
});

const adminSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/settings",
  component: AdminSettingsPage,
});

const adminTrafficRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/traffic",
  component: AdminTrafficPage,
});

const adminLoyaltyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/loyalty",
  component: AdminLoyaltyPage,
});

const adminMenuRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/menu",
  component: AdminMenuPage,
});
const adminPosRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/pos",
  component: AdminPosPage,
});
const adminPosKdsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/pos/kds",
  component: AdminPosKdsPage,
});
const adminPosOrdersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/pos/orders",
  component: AdminPosOrdersPage,
});
const adminFloorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/floor",
  component: AdminFloorPage,
});

const adminRevenueRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/revenue",
  component: AdminRevenuePage,
});

const adminCalendarRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/calendar",
  component: AdminCalendarPage,
});

const adminAnalyticsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/analytics",
  component: AdminAnalyticsPage,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/forgot-password",
  component: ForgotPasswordPage,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
  component: ResetPasswordPage,
});

const bookServiceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/book-service",
  component: BookServicePage,
});

const nfcTapRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/t/$token",
  component: NfcTapPage,
});

const preferencesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/preferences",
  component: PreferencesPage,
});

const adminNotificationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/notifications",
  component: AdminNotificationsPage,
});

const notificationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/notifications",
  component: NotificationsPage,
});

// A direct /festival/$id link is a "go buy this festival" entry point, so it
// renders the same minimal splash the venue root does (logo + sign-up/buy per
// the venue's configured mode), never the full lineup/marketing scroll. Reads
// the id from the route param (FestivalPage falls back to useParams).
const festivalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/festival/$id",
  component: () => <FestivalPage variant="splash" />,
});

const adminFestivalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/festival",
  component: AdminFestivalPage,
});

// Same component, non-festival-branded URL. Used by the per-event "Tickets
// & schedule" button on /admin/events so a HOSPITALITY / STUDIO / WELLNESS
// venue doesn't land on a page whose URL says "festival" (Fenton flagged
// this on Studio Two Point 2026-09-18: app.studiotwopoint.co.uk/admin/festival
// reads as wrong when the venue isn't a festival). The AdminFestivalPage
// component itself already branches on isFestivalVenue for its own header
// + copy, so the same URL/component works for both cases; only the URL
// name needed to change. /admin/festival stays for FESTIVAL nav + legacy
// bookmarks.
const adminEventTicketsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/event-tickets",
  component: AdminFestivalPage,
});

const adminWebsiteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/website",
  component: AdminWebsitePage,
});

const adminMailingListsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/mailing-lists",
  component: AdminMailingListsPage,
});

const adminOrdersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/orders",
  component: AdminOrdersPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  radioScheduleRoute,
  radioShowsRoute,
  radioShopRoute,
  radioSupportRoute,
  tiersRoute,
  benefitsRoute,
  downloadRoute,
  noticeboardRoute,
  trialRoute,
  signInRoute,
  signUpRoute,
  accountRoute,
  ticketsRoute,
  myTicketsRoute,
  festivalLineupRoute,
  festivalWhatsOnRoute,
  festivalInfoRoute,
  ticketDetailRoute,
  bookingsRoute,
  bookRoute,
  eventsRoute,
  galleryRoute,
  newsRoute,
  sitePageSplatRoute,
  eventDetailRoute,
  menuRoute,
  recordDetailRoute,
  cartRoute,
  wishlistRoute,
  adminInventoryRoute,
  adminStorefrontRoute,
  adminRadioRoute,
  adminOffersRoute,
  sellRoute,
  rewardsRoute,
  profileRoute,
  adminRoute,
  adminBrandingRoute,
  adminMediaRoute,
  adminSmsRoute,
  adminNoticeboardRoute,
  adminDiscountCodesRoute,
  adminBenefitsRoute,
  adminSubscribersRoute,
  adminTagsRoute,
  adminEventsRoute,
  adminEventAttendeesRoute,
  adminScanRoute,
  ticketPublicRoute,
  adminTeamRoute,
  adminServicesRoute,
  adminSessionsRoute,
  adminTrafficRoute,
  adminFansRoute,
  adminFanRoute,
  adminSegmentsRoute,
  adminCampaignsRoute,
  adminTrackedLinksRoute,
  adminWelcomeEmailRoute,
  adminSettingsRoute,
  adminLoyaltyRoute,
  adminMenuRoute,
  adminPosRoute,
  adminPosKdsRoute,
  adminPosOrdersRoute,
  adminFloorRoute,
  adminRevenueRoute,
  adminAnalyticsRoute,
  adminCalendarRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  bookServiceRoute,
  nfcTapRoute,
  preferencesRoute,
  adminNotificationsRoute,
  notificationsRoute,
  festivalRoute,
  adminFestivalRoute,
  adminEventTicketsRoute,
  adminWebsiteRoute,
  adminMailingListsRoute,
  adminOrdersRoute,
]);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
